import { redirect, useLoaderData } from "@remix-run/react"
import { LoaderFunctionArgs, MetaFunction } from "@vercel/remix"
import { useHydrateAtoms } from "jotai/utils"
import { aCaptions, aSentimentQuestions, aTopBanners } from "~/atoms"
import { Player } from "~/components"

import { buildVideoMeta, getBaseUrl } from "~/utils"
import { getS3SignedUrl } from "~/utils.server"
import { getSignedCaptions, getVideoOrThrow } from "~/utils.server/video"

export const loader = async ({ params }: LoaderFunctionArgs) => {
  const { id } = params
  if (!id) {
    throw new Response("Not Found", { status: 404 })
  }
  if (id === "59b547ba") {
    return redirect(`/embed/76be7bac`)
  }
  const video = await getVideoOrThrow(id)

  const { s3VideoKey } = video
  const captions = await getSignedCaptions(video)

  let videoUrl
  if (s3VideoKey) {
    videoUrl = await getS3SignedUrl(s3VideoKey, 60 * 60 * 24 * 7) // 7 days
  } else {
    videoUrl = `${getBaseUrl()}/${id}`
  }

  const videoUrlParts = new URL(videoUrl)
  const extension = videoUrlParts.pathname.split(".").pop()?.toLowerCase()

  const embedVideoUrl = `${getBaseUrl()}/video/loader.${extension}?id=${id}&videoUrl=${encodeURIComponent(videoUrl)}&rand=${Math.round(Math.random() * 1000000)}`
  return {
    name: video.name,
    description: video.description,
    url: `/embed/${id}/manifest.m3u8`,
    coverUrl: video.cover.secure_url,
    captions,
    topBanners: video.topBanners,
    videoUrl: embedVideoUrl,
    showFeedback: video.showFeedback,
    id,
    providerName: video.providerName,
    systemVersion: video.systemVersion,
    creationTimestamp: video.creationTimestamp,
    lessonId: video.lesson_id,
    createdBy: video.createdBy,
    sentimentQuestions: video.sentimentQuestions,
    course_id: video.course_id,
  }
}

export const meta: MetaFunction<typeof loader> = ({ data, params }) => {
  if (!data) {
    return [{ title: "Not Found" }]
  }
  return buildVideoMeta(`${getBaseUrl()}/${params.id}`, "video/mp4", data)
}

// Add custom element type declaration for TypeScript
declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      "data-protocol-video": React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement>,
        HTMLElement
      > & {
        width?: string
        ["video-id"]?: string
        ["unique-id"]?: string
      }
    }
  }
}

export default function VideoEmbed() {
  const {
    id,
    url,
    coverUrl,
    captions,
    topBanners,
    showFeedback,
    createdBy,
    sentimentQuestions,
    course_id,
  } = useLoaderData<typeof loader>()
  useHydrateAtoms([[aCaptions, captions]])
  useHydrateAtoms([[aTopBanners, topBanners]])
  useHydrateAtoms([[aSentimentQuestions, sentimentQuestions ?? []]])
  console.log(sentimentQuestions)
  const aiModified = createdBy === "created-ai" || createdBy === "modified-ai"

  return (
    <Player
      url={url}
      coverUrl={coverUrl}
      id={id}
      falseEmbed={true}
      showFeedback={showFeedback}
      aiModified={aiModified}
      courseId={course_id}
    />
  )
}
